/**
 * FmRiskWorkspace internal region subcomponents
 * Each region composes existing Fm* primitives (never clones them) and consumes
 * the muem9.18 token contract via FmRiskWorkspace.css. Severity colour is
 * resolved from tokens INSIDE these regions; no raw colour crosses the public
 * boundary.
 */
import React from 'react';
import { FmKeyFigures, type FmKeyFigure } from './FmKeyFigures';
import { FmChip } from './FmChip';
import { FmAccordion } from './FmAccordion';
import { FmSelector, type FmSelectorItem } from './FmSelector';
import { FmSelect } from './FmSelect';
import { FmListRow } from './FmContentPrimitives';
import { FmRowActions, type FmRowAction } from './FmRowActions';
import { FmHeading, FmText } from './FmComposition';
import { fmIconMap } from './icons';
import { SEVERITY_ROW_TONE } from './FmRiskWorkspace.severity';
import type {
  FmRiskAction,
  FmRiskAlertRow,
  FmRiskFilter,
  FmRiskIconId,
  FmRiskLabel,
  FmRiskMetric,
  FmRiskSegmentOption,
  FmRiskSeverity,
  FmRiskStatus,
  FmRiskSuppression,
} from './FmRiskWorkspace.types';

/* ── Data-only → primitive adapters (ReactNode resolution stays INSIDE the DS) ── */

/** Resolve a semantic icon id to a DS glyph. Pages never pass JSX icons. */
const resolveIcon = (iconId?: FmRiskIconId): React.ReactNode =>
  iconId ? fmIconMap[iconId] : undefined;

/** Adapt shell-owned data-only metrics to the primitive's stat shape. */
const adaptMetrics = (metrics: FmRiskMetric[]): FmKeyFigure[] =>
  metrics.map((metric) => ({
    id: metric.testId ?? metric.label,
    label: metric.label,
    value: metric.value,
    unit: metric.unit,
    footnote: metric.description,
    testId: metric.testId,
  }));

/** Adapt shell-owned data-only actions to governed FmRowActions descriptors. */
const adaptActions = (actions: FmRiskAction[]): FmRowAction[] =>
  actions.map((action) => ({
    id: action.id,
    label: action.label,
    intent: action.intent,
    icon: resolveIcon(action.iconId),
    disabledReason: action.disabledReason,
    confirm: action.confirm,
    onSelect: action.onSelect,
    'data-testid': action.testId,
  }));

/** Adapt shell-owned data-only segment options to the primitive's option shape. */
const adaptSegments = (options: FmRiskSegmentOption[]): FmSelectorItem[] =>
  options.map((option) => ({
    value: option.value,
    label: option.label,
    disabled: option.disabled,
    icon: resolveIcon(option.iconId),
    'data-testid': option.testId,
  }));

/** Severity badge: a token-coloured dot + neutral label. No raw colour crosses. */
export const SeverityBadge: React.FC<{
  severity: FmRiskSeverity;
  label: string;
  testId?: string;
}> = ({ severity, label, testId }) => (
  <span className="fm-risk-workspace__severity" data-severity={severity} data-testid={testId}>
    <span className="fm-risk-workspace__severity-dot" data-severity={severity} aria-hidden="true" />
    <span className="fm-risk-workspace__severity-label">{label}</span>
  </span>
);

const QuietLabelChip: React.FC<{
  label: FmRiskLabel;
  variant: 'mute' | 'info';
  testId?: string;
}> = ({ label, variant, testId }) => (
  <FmChip variant={variant} data-testid={testId}>
    {label.label}
    {label.value ? `: ${label.value}` : ''}
  </FmChip>
);

/* ── Status band + metric rail ──────────────────────────────────────────── */

export interface StatusRegionProps {
  title: string;
  eyebrow?: string;
  status?: FmRiskStatus;
  freshness?: FmRiskLabel;
  metrics?: FmRiskMetric[];
  testId?: string;
}

export const StatusRegion: React.FC<StatusRegionProps> = ({
  title,
  eyebrow,
  status,
  freshness,
  metrics,
  testId,
}) => (
  <div className="fm-risk-workspace__status" data-testid={testId}>
    <div className="fm-risk-workspace__status-head">
      <div className="fm-risk-workspace__status-heading">
        {eyebrow ? (
          <FmText as="span" size="fine" tone="muted" eyebrow>
            {eyebrow}
          </FmText>
        ) : null}
        <FmHeading level={2} visualLevel={3}>
          {title}
        </FmHeading>
        {status?.detail ? (
          <FmText as="p" size="caption" tone="muted">
            {status.detail}
          </FmText>
        ) : null}
      </div>
      <div className="fm-risk-workspace__status-labels">
        {status ? (
          <SeverityBadge
            severity={status.severity}
            label={status.label}
            testId={testId ? `${testId}-severity` : undefined}
          />
        ) : null}
        {freshness ? (
          <QuietLabelChip
            label={freshness}
            variant="mute"
            testId={testId ? `${testId}-freshness` : undefined}
          />
        ) : null}
      </div>
    </div>
    {metrics && metrics.length > 0 ? (
      <FmKeyFigures
        presentation="rail"
        items={adaptMetrics(metrics)}
        data-testid={testId ? `${testId}-metrics` : undefined}
      />
    ) : null}
  </div>
);

/* ── Filter/action rail + suppression counts ────────────────────────────── */

const FilterChips: React.FC<{
  filter: Extract<FmRiskFilter, { kind: 'chips' }>;
  testId?: string;
}> = ({ filter, testId }) => (
  <fieldset
    className="fm-risk-workspace__filter-chips"
    aria-label={filter.label}
    data-testid={testId}
  >
    {filter.chips.map((chip) => (
      <FmChip
        key={chip.id}
        as="button"
        variant={chip.active ? 'brand' : 'mute'}
        disabled={chip.disabled}
        aria-pressed={chip.active}
        onClick={() => filter.onToggle?.(chip.id)}
        data-testid={testId ? `${testId}-${chip.id}` : undefined}
      >
        {chip.severity ? (
          <span
            className="fm-risk-workspace__severity-dot"
            data-severity={chip.severity}
            aria-hidden="true"
          />
        ) : null}
        {chip.label}
        {typeof chip.count === 'number' ? (
          <span className="fm-risk-workspace__filter-count">{chip.count}</span>
        ) : null}
      </FmChip>
    ))}
  </fieldset>
);

const FilterControl: React.FC<{ filter: FmRiskFilter; testId?: string }> = ({ filter, testId }) => {
  const controlTestId = testId ? `${testId}-${filter.id}` : undefined;
  let control: React.ReactNode;
  if (filter.kind === 'segments') {
    control = (
      <FmSelector
        variant="segmented"
        value={filter.value ?? null}
        items={adaptSegments(filter.options)}
        onChange={(value) => filter.onChange?.(value)}
        label={filter.label}
        data-testid={controlTestId}
      />
    );
  } else if (filter.kind === 'select') {
    control = (
      <FmSelect<string>
        value={filter.value}
        options={filter.options}
        onChange={(value) => filter.onChange?.(value)}
        placeholder={filter.placeholder}
        aria-label={filter.label}
        controlWidth="md"
        data-testid={controlTestId}
      />
    );
  } else {
    control = <FilterChips filter={filter} testId={controlTestId} />;
  }

  return (
    <div className="fm-risk-workspace__filter">
      <FmText as="span" size="fine" tone="muted" eyebrow>
        {filter.label}
      </FmText>
      {control}
    </div>
  );
};

const SuppressionCounts: React.FC<{ suppression: FmRiskSuppression; testId?: string }> = ({
  suppression,
  testId,
}) => {
  const { hiddenCount, suppressedCount } = suppression;
  return (
    <p
      className="fm-risk-workspace__suppression"
      data-testid={testId ? `${testId}-suppression` : undefined}
    >
      {typeof hiddenCount === 'number' && hiddenCount > 0 ? (
        <span data-testid={testId ? `${testId}-hidden` : undefined}>
          {suppression.hiddenLabel ? `${suppression.hiddenLabel} ` : ''}
          {hiddenCount}
        </span>
      ) : null}
      {typeof suppressedCount === 'number' && suppressedCount > 0 ? (
        <span data-testid={testId ? `${testId}-suppressed` : undefined}>
          {suppression.suppressedLabel ? `${suppression.suppressedLabel} ` : ''}
          {suppressedCount}
        </span>
      ) : null}
    </p>
  );
};

export interface FilterRegionProps {
  filters?: FmRiskFilter[];
  filterLabel?: string;
  /** Disclosure label for the collapsed `secondary`-tier filters. */
  moreLabel?: string;
  suppression?: FmRiskSuppression;
  testId?: string;
}

/** Suppression counts, rendered only when there is a positive count to report. */
const renderSuppression = (suppression?: FmRiskSuppression, testId?: string): React.ReactNode => {
  const hidden = suppression?.hiddenCount;
  const suppressed = suppression?.suppressedCount;
  const has = (hidden != null && hidden > 0) || (suppressed != null && suppressed > 0);
  return has && suppression ? (
    <SuppressionCounts suppression={suppression} testId={testId} />
  ) : null;
};

/** A flat, wrapping group of filter controls (shared by the toolbar + disclosure). */
const FilterGroup: React.FC<{
  filters: FmRiskFilter[];
  label?: string;
  extra?: React.ReactNode;
  testId?: string;
}> = ({ filters, label, extra, testId }) => (
  <fieldset className="fm-risk-workspace__filters" aria-label={label}>
    {filters.map((filter) => (
      <FilterControl key={filter.id} filter={filter} testId={testId} />
    ))}
    {extra}
  </fieldset>
);

/** Secondary-tier filters collapsed behind a "More filters" disclosure. */
const SecondaryFilters: React.FC<{ filters: FmRiskFilter[]; label?: string; testId?: string }> = ({
  filters,
  label,
  testId,
}) => {
  if (filters.length === 0) return null;
  return (
    <FmAccordion
      plain
      className="fm-risk-workspace__filters-more"
      data-testid={testId ? `${testId}-more` : undefined}
      items={[
        {
          key: 'more',
          label,
          children: <FilterGroup filters={filters} label={label} testId={testId} />,
        },
      ]}
    />
  );
};

export const FilterRegion: React.FC<FilterRegionProps> = ({
  filters,
  filterLabel,
  moreLabel,
  suppression,
  testId,
}) => {
  const primary = filters?.filter((f) => f.tier !== 'secondary') ?? [];
  const secondary = filters?.filter((f) => f.tier === 'secondary') ?? [];
  const suppressionNode = renderSuppression(suppression, testId);
  if (primary.length === 0 && secondary.length === 0 && !suppressionNode) return null;

  return (
    <div className="fm-risk-workspace__filter-rail" data-testid={testId}>
      <FilterGroup filters={primary} label={filterLabel} extra={suppressionNode} testId={testId} />
      <SecondaryFilters filters={secondary} label={moreLabel} testId={testId} />
    </div>
  );
};

/* ── Ranked alert list ──────────────────────────────────────────────────── */

const AlertRowMeta: React.FC<{ row: FmRiskAlertRow }> = ({ row }) => {
  if (!row.hazard && !row.meta && !row.freshness) return null;
  return (
    <span className="fm-risk-workspace__row-meta">
      {row.hazard ? <span>{row.hazard}</span> : null}
      {row.meta ? <span>{row.meta}</span> : null}
      {row.freshness ? <span>{row.freshness}</span> : null}
    </span>
  );
};

interface AlertRowProps {
  row: FmRiskAlertRow;
  selected: boolean;
  onSelect?: (id: string) => void;
  actionsLabel: string;
  overflowLabel: string;
  testId?: string;
}

const AlertRow: React.FC<AlertRowProps> = ({
  row,
  selected,
  onSelect,
  actionsLabel,
  overflowLabel,
  testId,
}) => (
  <FmListRow
    selectable
    selected={selected}
    onSelect={onSelect ? () => onSelect(row.id) : undefined}
    tone={SEVERITY_ROW_TONE[row.severity]}
    data-testid={testId ? `${testId}-row-${row.id}` : undefined}
    leading={
      <span
        className="fm-risk-workspace__severity-dot"
        data-severity={row.severity}
        aria-hidden="true"
      />
    }
    title={row.title}
    meta={<AlertRowMeta row={row} />}
    status={
      row.value !== undefined ? (
        <span className="fm-risk-workspace__row-value">
          {row.value}
          {row.unit ? <span className="fm-risk-workspace__row-unit"> {row.unit}</span> : null}
        </span>
      ) : undefined
    }
    actions={
      row.actions && row.actions.length > 0 ? (
        <FmRowActions
          actions={adaptActions(row.actions)}
          label={actionsLabel}
          overflowLabel={overflowLabel}
          data-testid={testId ? `${testId}-row-${row.id}-actions` : undefined}
        />
      ) : undefined
    }
  />
);

/**
 * Explicit empty state. NO-FALLBACK: the label is REQUIRED — callers render this
 * ONLY when they supply localized copy, never an em-dash placeholder.
 */
const EmptyState: React.FC<{ label: string; testId?: string }> = ({ label, testId }) => (
  <div className="fm-risk-workspace__empty" data-state="empty" data-testid={testId}>
    {label}
  </div>
);

export interface AlertListProps {
  listTitle?: string;
  rows?: FmRiskAlertRow[];
  selectedId?: string;
  onSelectRow?: (id: string) => void;
  emptyLabel?: string;
  actionsLabel: string;
  overflowLabel: string;
  testId?: string;
}

export const AlertList: React.FC<AlertListProps> = ({
  listTitle,
  rows,
  selectedId,
  onSelectRow,
  emptyLabel,
  actionsLabel,
  overflowLabel,
  testId,
}) => (
  <section className="fm-risk-workspace__list" data-testid={testId} aria-label={listTitle}>
    {listTitle ? (
      <FmHeading level={3} visualLevel={4} className="fm-risk-workspace__region-title">
        {listTitle}
      </FmHeading>
    ) : null}
    {rows && rows.length > 0 ? (
      <FmListRow.Group selectable={true} data-testid={testId ? `${testId}-rows` : undefined}>
        {rows.map((row) => (
          <AlertRow
            key={row.id}
            row={row}
            selected={row.id === selectedId}
            onSelect={onSelectRow}
            actionsLabel={actionsLabel}
            overflowLabel={overflowLabel}
            testId={testId}
          />
        ))}
      </FmListRow.Group>
    ) : emptyLabel ? (
      <EmptyState label={emptyLabel} testId={testId ? `${testId}-empty` : undefined} />
    ) : null}
  </section>
);

export { DetailPane } from './FmRiskWorkspace.detail';
export type { DetailPaneProps } from './FmRiskWorkspace.detail';
